import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { DateTime } from 'luxon'

import {
  getQueryParamsFromHash,
  parsePermittedQueryParams,
  setQueryParamsToHash,
} from '@domain-group/javascript-utils'
import { useSetState, useWatchEntityUpdates } from '@domain-group/react-hooks'

import handleCallbackAction from '@functions/handleCallbackAction'
import { getDeadlineStatuses } from '@functions/publicationEdition'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useLayoutTemplates from '@hooks/useLayoutTemplates'
import useOrders from '@hooks/useOrders'
import usePublication from '@hooks/usePublication'
import usePublicationBooking from '@hooks/usePublicationBooking'
import usePublicationBookingItems from '@hooks/usePublicationBookingItems'
import usePublicationBookings from '@hooks/usePublicationBookings'
import usePublicationEdition from '@hooks/usePublicationEdition'
import usePublicationOrder from '@hooks/usePublicationOrder'
import useSelector from '@hooks/useSelector'

import type { UsePublicationBookingPayload } from '@hooks/usePublicationBooking'
import type { UsePublicationOrderPayload } from '@hooks/usePublicationOrder'
import type { EntitiesState } from '@redux/entities'
import type {
  OrderItemModel,
  PublicationBookingModel,
  PublicationEditionModel,
  PublicationModel,
  PublicationOrderRequestOptions,
} from '@models/types'

const availableZooms = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.25, 2.5, 2.75, 3, 4, 5, 6, 10]
const watchEntityKeys = ['agencies']

export const defaultRequestOptions = {
  artwork: {
    include_artwork_preview: true,
  },
  order: {
    exclude_hidden_campaigns: true,
    include_order_agency: true,
    include_order_campaign: true,
    include_order_items: true,
  },
  orderItem: {
    include_order_item_service_product: true,
  },
  publicationBooking: {
    include_publication_booking_items: true,
    include_publication_booking_publication_product: true,
    include_publication_booking_space: true,
    include_publication_booking_status: true,
  },
  publicationBookingItem: {
    include_publication_booking_item_artwork: true,
    include_publication_booking_item_grid: true,
  },
}

const padding = {
  width: 375,
  height: 70,
}

type SortItemKey = 'agency' | 'price-asc' | 'price-desc' | 'size' | 'suburb'

type DefaultState = {
  buildingArtworks: boolean,
  filterString: string,
  pollIntervalId?: number,
  sortItemsByKey: SortItemKey,
  zoom: number,
}

const defaultState: DefaultState = {
  buildingArtworks: false,
  filterString: '',
  pollIntervalId: undefined,
  sortItemsByKey: 'size',
  zoom: 1,
}

type SetState = (state: Partial<typeof defaultState>) => void

type ArtworksReadyParams = {
  callbacks: {
    setState: SetState,
    showApprovePublicationBookingModal: () => void,
  },
  stateRef: {
    current: DefaultState,
  },
}

const artworksReady = (params: ArtworksReadyParams) => {
  const {
    callbacks: {
      setState,
      showApprovePublicationBookingModal,
    },
    stateRef: {
      current: {
        pollIntervalId,
      },
    },
  } = params

  if (pollIntervalId){
    clearInterval(pollIntervalId)
  }

  setState({
    buildingArtworks: false,
    pollIntervalId: undefined,
  })

  showApprovePublicationBookingModal()
}

type PollCheckBookingArtworksParams = {
  callbacks: {
    checkPublicationBookingArtworks: UsePublicationBookingPayload['callbacks']['checkPublicationBookingArtworks'],
    showApprovePublicationBookingModal: () => void,
    setState: SetState,
  },
  stateRef: {
    current: DefaultState,
  },
}

const pollCheckBookingArtworks = async (params: PollCheckBookingArtworksParams) => {
  const {
    callbacks: {
      checkPublicationBookingArtworks,
      showApprovePublicationBookingModal,
      setState,
    },
    stateRef,
  } = params

  const result = await checkPublicationBookingArtworks()
  // @ts-expect-error TS2339
  if (result.data?.ready){
    return artworksReady({
      callbacks: {
        setState,
        showApprovePublicationBookingModal,
      },
      stateRef,
    })
  }

  return false
}

type CheckAndPollBookingArtworksParams = {
  callbacks: {
    artworksBuilding: VoidFunction,
    checkPublicationBookingArtworks: UsePublicationBookingPayload['callbacks']['checkPublicationBookingArtworks'],
    showApprovePublicationBookingModal: () => void,
    setState: SetState,
  },
  stateRef: {
    current: DefaultState,
  },
}

const checkAndPollBookingArtworks = async (params: CheckAndPollBookingArtworksParams) => {
  const {
    callbacks: {
      artworksBuilding,
      checkPublicationBookingArtworks,
      showApprovePublicationBookingModal,
      setState,
    },
    stateRef,
  } = params

  // Check if artworks are ready
  const initialResult = await checkPublicationBookingArtworks()
  // @ts-expect-error TS2339
  if (initialResult.data?.ready){
    return artworksReady({
      callbacks: {
        setState,
        showApprovePublicationBookingModal,
      },
      stateRef,
    })
  }

  // If artworks aren't ready, poll the server to check every 15 seconds
  const pollIntervalId = setInterval(() => {
    pollCheckBookingArtworks({
      callbacks: {
        checkPublicationBookingArtworks,
        setState,
        showApprovePublicationBookingModal,
      },
      stateRef,
    })
  }, 15000)

  setState({
    buildingArtworks: true,
    // @ts-expect-error TS2322
    pollIntervalId,
  })

  return artworksBuilding()
}

type CheckRemovedBookingItemsParams = {
  callbacks: {
    removedBookingItems: UsePublicationOrderPayload['callbacks']['removedBookingItems'],
    showCreatePublicationOrderModal: VoidFunction,
    showReviewRemovedPublicationBookingItemsModal: (payload: object) => void,
  },
  publicationOrderId: number,
}

const checkRemovedBookingItems = (params: CheckRemovedBookingItemsParams) => {
  const {
    callbacks: {
      removedBookingItems,
      showCreatePublicationOrderModal,
      showReviewRemovedPublicationBookingItemsModal,
    },
    publicationOrderId,
  } = params

  if (publicationOrderId){
    const payload = {
      publicationOrderId,
      requestOptions: {
        include_publication_booking_item_booking: true,
        include_publication_booking_item_campaign: true,
        include_publication_booking_item_order: true,
        include_publication_booking_item_product: true,
      },
    }

    return removedBookingItems(payload).then((response) => {
      // @ts-expect-error TS2339
      const { data: publicationBookingItems, success } = response

      if (success && publicationBookingItems?.length > 0){
        return (
          showReviewRemovedPublicationBookingItemsModal(showCreatePublicationOrderModal)
        )
      }

      return showCreatePublicationOrderModal()
    })
  }

  return showCreatePublicationOrderModal()
}

type GetSelectedBookingIndexParams = {
  publicationBookings: PublicationBookingModel[],
  selectedPublicationBooking: PublicationBookingModel,
}

const getSelectedBookingIndex = (params: GetSelectedBookingIndexParams) => {
  const {
    publicationBookings,
    selectedPublicationBooking,
  } = params

  const index = publicationBookings.findIndex(publicationBooking => (
    publicationBooking.id === selectedPublicationBooking.id
  ))

  return index
}

type FilterOrderItemsParams = {
  agencies: EntitiesState['agencies'],
  campaigns: EntitiesState['campaigns'],
  filterString: string,
  orderItems: OrderItemModel[],
  orders: EntitiesState['orders'],
  publicationEditions: EntitiesState['publicationEditions'],
  selectedPublication: PublicationModel,
  selectedPublicationEdition: PublicationEditionModel,
}

const filterOrderItems = (params: FilterOrderItemsParams) => {
  const {
    agencies,
    campaigns,
    filterString,
    orders,
    orderItems,
    publicationEditions,
    selectedPublication,
    selectedPublicationEdition,
  } = params

  const compatiblePublicationIds = selectedPublication.compatible_pagination_ids || []

  return orderItems.filter((orderItem) => {
    const order = orders[orderItem.order_id] || {}
    const agency = agencies[order.agency_id] || {}
    const campaign = campaigns[order.campaign_id] || {}
    // @ts-expect-error TS2538
    const orderEdition = publicationEditions[order.publication_edition_id] || {}

    const compatiblePagination = (
      // @ts-expect-error TS2345
      (compatiblePublicationIds && compatiblePublicationIds.includes(order.publication_id))
      || order.publication_id === selectedPublication.id
    )

    if (
      compatiblePagination
      && selectedPublicationEdition.publication_date === orderEdition.publication_date
      && (
        (campaign.address && campaign.address.toLowerCase().includes(filterString.toLowerCase()))
        || (agency.title && agency.title.toLowerCase().includes(filterString.toLowerCase()))
      )
    ){
      return true
    }

    return false
  })
}

type GetSortValueParams = {
  agencies: EntitiesState['agencies'],
  campaigns: EntitiesState['campaigns'],
  orders: EntitiesState['orders'],
  serviceProducts: EntitiesState['serviceProducts'],
  sortItemsByKey: SortItemKey,
  orderItem: OrderItemModel,
}

const getSortValue = (params: GetSortValueParams) => {
  const {
    agencies,
    campaigns,
    orderItem,
    orders,
    serviceProducts,
    sortItemsByKey,
  } = params

  const order = orders[orderItem.order_id]

  // Sort by Ad Size
  if (sortItemsByKey === 'size'){
    // @ts-expect-error TS2538
    const product = serviceProducts[orderItem.service_product_id]
    if (product?.options?.publication?.grid
    ){
      const { grid } = product.options.publication
      return parseInt(grid.rows, 10) * parseInt(grid.columns, 10)
    }

    return 1
  }

  // Sort by Campaign Suburb
  if (sortItemsByKey === 'suburb'){
    const campaign = campaigns[order.campaign_id]
    if (campaign?.suburb_name){
      return campaign.suburb_name
    }

    return 'ZZZZZZ'
  }

  // Sort by Agency Name
  if (sortItemsByKey === 'agency'){
    const agency = agencies[order.agency_id]
    if (agency?.title){
      return agency.title
    }

    return 'ZZZZZZ'
  }

  // Sort by Price
  if (sortItemsByKey === 'price-asc' || sortItemsByKey === 'price-desc'){
    const { total } = order
    if (total){
      return sortItemsByKey === 'price-asc' ? -total : total
    }

    return sortItemsByKey === 'price-asc' ? -9999999 : -1
  }

  return null
}

type SortOrderItemsParams = {
  agencies: EntitiesState['agencies'],
  campaigns: EntitiesState['campaigns'],
  orderItems: OrderItemModel[],
  orders: EntitiesState['orders'],
  serviceProducts: EntitiesState['serviceProducts'],
  sortItemsByKey: SortItemKey,
}

const sortOrderItems = (params: SortOrderItemsParams) => {
  const {
    agencies,
    campaigns,
    orderItems,
    orders,
    serviceProducts,
    sortItemsByKey,
  } = params

  return orderItems.sort((a, b) => {
    const sortA = getSortValue({
      agencies,
      campaigns,
      orderItem: a,
      orders,
      serviceProducts,
      sortItemsByKey,
    })

    const sortB = getSortValue({
      agencies,
      campaigns,
      orderItem: b,
      orders,
      serviceProducts,
      sortItemsByKey,
    })

    if (sortItemsByKey === 'agency' || sortItemsByKey === 'suburb'){
      // @ts-expect-error TS18047
      if (sortA < sortB) return -1
      // @ts-expect-error TS18047
      if (sortA > sortB) return 1
      return 0
    }

    // @ts-expect-error TS18047
    if (sortA > sortB) return -1
    // @ts-expect-error TS18047
    if (sortA < sortB) return 1
    return 0
  })
}

type BuildGroupedEditionDataParams = {
  artworks: EntitiesState['artworks'],
  publicationBookingItems: EntitiesState['publicationBookingItems'],
  publicationBookings: EntitiesState['publicationBookings'],
  selectedPublicationEditionId?: number,
  statuses: EntitiesState['statuses'],
}

type EditionBookingData = {
  approvedArtworkIds: number[],
  artworksFilled: boolean,
  hasBookingNumber: boolean,
  isReleased: boolean,
  itemCount: number,
  pendingCancellationIds: number[],
}

type GroupedEditionData = {
  [key: number]: {
    allArtworksApproved: boolean,
    allConfirmationsFilled: boolean,
    allPagesReleased: boolean,
    allSpaceFilled: boolean,
    approvedArtworkIds: number[],
    availableSpace: number,
    bookingCount: number,
    bookingData: {
      [key: number]: EditionBookingData,
    },
    bookingNumbers: string[],
    isBooked: boolean,
    isPending: boolean,
    itemCount: number,
    placedOrderItemIds: [],
    publicationOrderId: null | number,
    releasedBookingIds: number[],
    pendingCancellationIds: number[],
    usedSpace: number,
  },
}

const buildGroupedEditionData = (params: BuildGroupedEditionDataParams) => {
  const {
    artworks,
    publicationBookingItems,
    publicationBookings,
    selectedPublicationEditionId,
    statuses,
  } = params

  if (!selectedPublicationEditionId){
    return {}
  }

  // Setup Groups
  const groupedEditionData = Object.values(publicationBookings).reduce((acc, publicationBooking) => {
    const {
      available_space,
      booking_confirmation_id,
      publication_edition_id: editionId,
      publication_order_id,
      status_id,
    } = publicationBooking

    const bookingStatus = status_id && statuses[status_id] ? statuses[status_id] : {}

    if (editionId !== selectedPublicationEditionId){
      return acc
    }

    // Setup Group
    if (!acc[editionId]){
      acc[editionId] = {
        allArtworksApproved: false,
        allConfirmationsFilled: false,
        allPagesReleased: false,
        allSpaceFilled: false,
        approvedArtworkIds: [],
        availableSpace: 0,
        bookingCount: 0,
        bookingData: {},
        bookingNumbers: [],
        isBooked: false,
        isPending: true,
        itemCount: 0,
        pendingCancellationIds: [],
        placedOrderItemIds: [],
        publicationOrderId: null,
        releasedBookingIds: [],
        usedSpace: 0,
      }
    }

    acc[editionId].bookingCount += 1

    // Available Space
    acc[editionId].availableSpace += available_space

    // Booked - If one is booked, all are booked
    // @ts-expect-error TS2339
    if (bookingStatus.key === 'approved' || bookingStatus.key === 'booked'){
      acc[editionId].isBooked = true
      acc[editionId].isPending = false
    }

    const filteredBookingItems = Object.values(publicationBookingItems).filter((publicationBookingItem) => {
      const { publication_booking_id } = publicationBookingItem
      return publication_booking_id === publicationBooking.id
    })

    // Booking Data
    acc[editionId].bookingData[publicationBooking.id] = {
      approvedArtworkIds: [],
      artworksFilled: false,
      hasBookingNumber: !!booking_confirmation_id,
      isReleased: false,
      itemCount: filteredBookingItems.length,
      pendingCancellationIds: [],
    }

    // Approved
    // @ts-expect-error TS2339
    if (bookingStatus.key === 'approved'){
      acc[editionId].bookingData[publicationBooking.id].isReleased = true
      acc[editionId].releasedBookingIds.push(publicationBooking.id)
    }

    // Booking Numbers
    // @ts-expect-error TS2339
    if (booking_confirmation_id?.length){
      // @ts-expect-error TS2345
      acc[editionId].bookingNumbers.push(booking_confirmation_id)
    }

    // Publication Order
    if (!acc[editionId].publicationOrderId && publication_order_id){
      acc[editionId].publicationOrderId = publication_order_id
    }

    // Booking Items
    filteredBookingItems.forEach((publicationBookingItem) => {
      // Placed Order Items
      // @ts-expect-error TS2345
      acc[editionId].placedOrderItemIds.push(publicationBookingItem.order_item_id)

      // Total Item Count
      acc[editionId].itemCount += 1

      // Used Space
      // @ts-expect-error TS2339
      if (publicationBookingItem.grid){
        acc[editionId].usedSpace += (
          // @ts-expect-error TS2339
          Number(publicationBookingItem.grid.columns) * Number(publicationBookingItem.grid.rows)
        )
      }

      // Artworks
      // @ts-expect-error TS2339
      const artwork = artworks[publicationBookingItem.artwork]
      const artworkStatus = artwork ? statuses[artwork.status_id] : null

      if (artwork && artworkStatus && artworkStatus.key === 'approved'){
        acc[editionId].bookingData[publicationBooking.id].approvedArtworkIds.push(artwork.id)
        acc[editionId].approvedArtworkIds.push(artwork.id)
      }

      acc[editionId].bookingData[publicationBooking.id].artworksFilled = (
        acc[editionId].bookingData[publicationBooking.id].approvedArtworkIds.length === acc[editionId].bookingData[publicationBooking.id].itemCount
      )

      // Pending Cancellations
      if (publicationBookingItem.pending_cancellation){
        acc[editionId].pendingCancellationIds.push(publicationBookingItem.id)
        acc[editionId].bookingData[publicationBooking.id].pendingCancellationIds.push(publicationBookingItem.id)
      }
    })

    // Is all space on the layout grid filled?
    acc[editionId].allSpaceFilled = acc[editionId].availableSpace === acc[editionId].usedSpace

    // Do all pages have confirmation ids?
    acc[editionId].allConfirmationsFilled = (
      acc[editionId].bookingCount === acc[editionId].bookingNumbers.length
    )

    // Are all artworks are approved?
    acc[editionId].allArtworksApproved = (
      acc[editionId].itemCount === acc[editionId].approvedArtworkIds.length
    )

    // Are all pages released?
    acc[editionId].allPagesReleased = (
      acc[editionId].bookingCount === acc[editionId].releasedBookingIds.length
    )

    return acc
  }, {} as GroupedEditionData)

  return groupedEditionData
}

const getStoredZoom = (localStorageKey: string) => {
  const storedData = localStorage.getItem(localStorageKey)
  if (storedData){
    const parsedData = JSON.parse(storedData)
    return parsedData.zoom
  }

  return null
}

const calculateOptimalZoom = (templatePageHeight: number, templatePageWidth: number) => {
  let zoom = 1

  if (templatePageHeight && templatePageWidth){
    const windowSize = {
      width: window.innerWidth - padding.width,
      height: window.innerHeight - padding.height,
    }

    const ratio = {
      width: windowSize.width / templatePageWidth,
      height: windowSize.height / templatePageHeight,
    }

    const selectedRatio = ratio.width < ratio.height ? ratio.width : ratio.height

    zoom = availableZooms.reduce((prev, current) => (
      Math.abs(current - selectedRatio) < Math.abs(prev - selectedRatio) ? current : prev
    ))

    // Drop a zoom level if the template is bigger than the screen
    if (windowSize.width < templatePageWidth * zoom || windowSize.height < templatePageHeight * zoom){
      const index = availableZooms.indexOf(zoom)
      zoom = index > 0 ? availableZooms[index - 1] : availableZooms[index]
    }
  }

  return zoom
}

type ChangeZoomParams = {
  localStorageKey: string,
  zoom: number,
  setState: SetState,
}

const changeZoom = (params: ChangeZoomParams) => {
  const { localStorageKey, zoom, setState } = params

  // Store in localStorage
  localStorage.setItem(localStorageKey, JSON.stringify({ zoom }))

  // Update State
  setState({
    zoom,
  })
}

type ZoomActionType = 'increase' | 'decrease'

type PerformZoomActionParams = {
  localStorageKey: string,
  actionType: ZoomActionType,
  state: DefaultState,
  setState: SetState,
}

const performZoomAction = (params: PerformZoomActionParams) => {
  const {
    localStorageKey, actionType, state: { zoom }, setState,
  } = params

  const index = availableZooms.indexOf(zoom)
  const newIndex = actionType === 'increase' ? index + 1 : index - 1

  const newZoom = availableZooms[newIndex] || zoom

  changeZoom({ localStorageKey, zoom: newZoom, setState })
}

type SelectLayoutTemplateIdParams = {
  payload: HandleCallbackActionParams,
  publicationBooking: PublicationBookingModel,
  updatePublicationBooking: UsePublicationBookingPayload['callbacks']['updatePublicationBooking'],
}

const selectLayoutTemplateId = (params: SelectLayoutTemplateIdParams) => {
  const {
    payload,
    publicationBooking,
    updatePublicationBooking,
  } = params

  return handleCallbackAction({
    ...payload,
    callbacks: {
      ...payload.callbacks,
      // @ts-expect-error TS2322
      action: updatePublicationBooking,
    },
    entityParams: {
      ...publicationBooking,
      ...payload.entityParams,
    },
    requestOptions: {
      ...defaultRequestOptions.artwork,
      ...defaultRequestOptions.publicationBooking,
      ...defaultRequestOptions.publicationBookingItem,
    },
  })
}

const selectPublicationId = (payload: HandleCallbackActionParams) => {
  const { callbacks, entityParams } = payload

  setQueryParamsToHash({
    // @ts-expect-error TS2339
    publicationId: entityParams?.publication_id,
  })
  // @ts-expect-error TS2339
  if (callbacks.afterAction) callbacks.afterAction()
}

const selectPublicationEditionId = (payload: HandleCallbackActionParams) => {
  const { callbacks, entityParams } = payload

  setQueryParamsToHash({
    // @ts-expect-error TS2339
    publicationEditionId: entityParams?.publication_edition_id,
    // @ts-expect-error TS2339
    publicationId: entityParams?.publication_id,
  })
  // @ts-expect-error TS2339
  if (callbacks.afterAction) callbacks.afterAction()
}

const selectPublicationBookingId = (payload: HandleCallbackActionParams) => {
  const { entityParams } = payload

  setQueryParamsToHash({
    // @ts-expect-error TS2339
    publicationBookingId: entityParams?.id,
    // @ts-expect-error TS2339
    publicationEditionId: entityParams?.publication_edition_id,
    // @ts-expect-error TS2339
    publicationId: entityParams?.publication_id,
  })
}

type PermittedQueryParams = {
  publicationBookingId?: number,
  publicationEditionId?: number,
  publicationId?: number,
}

type UsePublicationBookingPlannerOptions = {
  callbacks: {
    showApprovePublicationBookingModal: (payload: {
      isBulkRelease?: boolean,
      publicationBooking: PublicationBookingModel,
    }) => void,
    showCreateCampaignPublicationOrderModal: (payload: object) => void,
    showCreateOrEditPublicationBookingModal: (payload: object) => void,
    showCreatePublicationOrderModal: (payload: object) => void,
    showManagePlannerBookingModal: (payload: object) => void,
    showReviewRemovedPublicationBookingItemsModal: (payload: object) => void,
    showSelectLayoutTemplateModal: (payload: object) => void,
    showSelectPublicationModal: (payload: object) => void,
    showSelectPublicationEditionModal: (payload: object) => void,
  },
}

function usePublicationBookingPlanner(options: UsePublicationBookingPlannerOptions) {
  const {
    callbacks: {
      showApprovePublicationBookingModal,
      showCreateCampaignPublicationOrderModal,
      showCreateOrEditPublicationBookingModal,
      showCreatePublicationOrderModal,
      showManagePlannerBookingModal,
      showReviewRemovedPublicationBookingItemsModal,
      showSelectLayoutTemplateModal,
      showSelectPublicationModal,
      showSelectPublicationEditionModal,
    },
  } = options

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const {
    updatedEntities: { publicationBookings: publicationBookingsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const [state, setState] = useSetState(defaultState)
  const { filterString, sortItemsByKey, zoom } = state

  const stateRef = useRef(state)
  stateRef.current = state

  const queryParams: PermittedQueryParams = parsePermittedQueryParams(
    getQueryParamsFromHash(),
    ['publicationId', 'publicationBookingId', 'publicationEditionId'],
  )
  const { publicationId, publicationBookingId, publicationEditionId } = queryParams

  // Entities
  const entitiesPayload = useSelector(reduxState => reduxState.entities)
  const {
    agencies,
    artworks,
    campaigns,
    orderItems,
    orders,
    publicationBookingItems,
    publicationBookings,
    publicationEditions,
    publications,
    serviceProducts,
    statuses,
  } = entitiesPayload

  const selectedPublication = publicationId && publications[publicationId] ? publications[publicationId] : {}
  const selectedPublicationEdition = publicationEditionId && publicationEditions[publicationEditionId]
    ? publicationEditions[publicationEditionId] : {}

  // @ts-expect-error TS2339
  const publicationDate = selectedPublicationEdition.publication_date
    // @ts-expect-error TS2339
    ? DateTime.fromFormat(selectedPublicationEdition.publication_date, 'dd/MM/yyyy').toFormat('yyyy-MM-dd')
    : undefined

  // TODO - This doesn't need to be hard coded
  const height = 800
  const width = 200

  // Calculate Optimal Zoom or Stored Zoom for Page
  const localStorageKey = 'PublicationPlannerZoom'

  useEffect(() => {
    const storedZoom = getStoredZoom(localStorageKey)
    if (storedZoom){
      setState({
        zoom: storedZoom,
      })
      return
    }

    if (height && width){
      const optimalZoom = calculateOptimalZoom(height, width)
      setState({
        zoom: optimalZoom,
      })
    }
  }, [localStorageKey, height, width])

  // Show Select Publication Modal
  useEffect(() => {
    if (!publicationId && showSelectPublicationModal){
      showSelectPublicationModal({
        callbacks: {
          selectPublicationId,
        },
      })
    }
  }, [publicationId])

  // Show Select Publication Edition Modal
  useEffect(() => {
    if (publicationId && !publicationEditionId && showSelectPublicationEditionModal){
      showSelectPublicationEditionModal({
        callbacks: {
          selectPublicationEditionId,
        },
        publicationId,
      })
    }
  }, [publicationId, publicationEditionId])

  // Publication
  const publicationPayload = usePublication({ id: publicationId }, {
    performHttpRequests: !!publicationId,
    requestOptions: {
      include_publication_brand_group_admin_member: true,
      include_publication_compatible_pagination_ids: true,
      include_publication_logo: true,
      include_publication_option_values: true,
      include_publication_options: true,
      include_publication_price_lists: true,
      include_publication_products: true,
    },
  })

  // Publication Edition
  const publicationEditionPayload = usePublicationEdition({ id: publicationEditionId }, {
    performHttpRequests: !!publicationEditionId,
    requestOptions: {
      include_publication_edition_deadlines: true,
    },
  })

  const { publicationEdition } = publicationEditionPayload

  const deadlineStatuses = getDeadlineStatuses(publicationEdition)

  // Orders
  const ordersPayload = useOrders({
    filters: {
      compatible_publication_id: publicationId,
      order_type: 'publication',
      // @ts-expect-error TS2561
      publication_date: publicationDate,
    },
    performHttpRequests: !!publicationId && !!publicationEditionId && !!publicationDate,
    requestOptions: {
      ...defaultRequestOptions.order,
      ...defaultRequestOptions.orderItem,
      // @ts-expect-error TS2353
      limit: 100,
      publication_date: publicationDate,
      order_by: 'updated_at desc',
    },
    sortBy: 'cache_key desc',
  })
  const { filteredOrders } = ordersPayload

  // Order Items
  const orderItemsByOrders = filteredOrders.reduce((acc, order) => {
    const { items: orderItemIds = [] } = order

    orderItemIds.forEach((orderItemId) => {
      const orderItem = orderItems[orderItemId]
      if (orderItem){
        acc.push(orderItem)
      }
    })

    return acc
  }, [] as OrderItemModel[])

  const filteredOrderItems = filterOrderItems({
    agencies,
    campaigns,
    filterString,
    orderItems: orderItemsByOrders,
    orders,
    publicationEditions,
    // @ts-expect-error TS2739
    selectedPublication,
    // @ts-expect-error TS2740
    selectedPublicationEdition,
  })

  sortOrderItems({
    agencies,
    campaigns,
    orderItems: filteredOrderItems,
    orders,
    serviceProducts,
    sortItemsByKey,
  })

  // Publication Bookings
  const publicationBookingsPayload = usePublicationBookings({
    filters: {
      order_by: 'publication_bookings.sort asc',
      publication_id: publicationId,
      publication_edition_id: publicationEditionId,
    },
    performHttpRequests: !!publicationId && !!publicationEditionId,
    requestOptions: {
      ...defaultRequestOptions.artwork,
      ...defaultRequestOptions.publicationBooking,
      ...defaultRequestOptions.publicationBookingItem,
    },
  })

  const { filteredPublicationBookings } = publicationBookingsPayload

  // Publication Booking Items
  const publicationBookingItemsPayload = usePublicationBookingItems({
    filters: {
      publication_booking_id: publicationBookingId,
    },
    performHttpRequests: false,
  })

  const publicationOrderPayload = usePublicationOrder()
  const {
    callbacks: {
      autoPaginate,
      removedBookingItems,
    },
  } = publicationOrderPayload

  // Layout Templates
  const layoutTemplatesPayload = useLayoutTemplates({
    filters: {
      publication_id: publicationId,
    },
    performHttpRequests: !!publicationId,
    requestOptions: {
      include_layout_template_compatible_products: true,
      include_layout_template_content_grid: true,
      include_layout_template_dimensions: true,
      include_layout_template_permission_ids: true,
      include_layout_template_preview: true,
    },
  })

  // Auto Select Booking
  useEffect(() => {
    if (publicationId && publicationEditionId && filteredPublicationBookings.length && !publicationBookingId){
      // @ts-expect-error TS2345
      selectPublicationBookingId({
        entityParams: {
          id: filteredPublicationBookings[0]?.id,
          publication_edition_id: publicationEditionId,
          publication_id: publicationId,
        },
      })
    }
  }, [publicationBookingId, publicationId, publicationEditionId, filteredPublicationBookings.length])

  const selectedPublicationBooking = publicationBookingId && publicationBookings[publicationBookingId]
    ? publicationBookings[publicationBookingId]
    : {}

  const publicationBookingPayload = usePublicationBooking(selectedPublicationBooking, {
    performHttpRequests: !!publicationBookingId,
    requestOptions: {
      ...defaultRequestOptions.artwork,
      ...defaultRequestOptions.publicationBooking,
      ...defaultRequestOptions.publicationBookingItem,
    },
  })

  const {
    callbacks: {
      checkPublicationBookingArtworks,
      createPublicationBooking,
      deletePublicationBooking,
      updatePublicationBooking,
    },
    publicationBooking,
  } = publicationBookingPayload

  // Grouped Edition Data
  const groupedEditionData = buildGroupedEditionData({
    artworks,
    publicationBookings,
    publicationBookingItems,
    // @ts-expect-error TS2353
    publicationEditions,
    selectedPublicationEditionId: publicationEditionId,
    statuses,
  })

  // @ts-expect-error TS2538
  const editionData = groupedEditionData[publicationEditionId] || {}
  const selectedBookingData = editionData?.bookingData?.[publicationBookingId] || {}

  return {
    availableZooms,
    callbacks: {
      autoPaginate: ({}, requestOptions: PublicationOrderRequestOptions) => autoPaginate({
        callbacks: {
          selectPublicationBookingId: (payload: HandleCallbackActionParams) => selectPublicationBookingId(payload),
        },
        publicationEditionId,
        requestOptions,
      }),
      checkAndPollBookingArtworks: () => checkAndPollBookingArtworks({
        callbacks: {
          artworksBuilding: () => console.log('TODO - show toast when artworks are still building in background'),
          checkPublicationBookingArtworks,
          setState,
          showApprovePublicationBookingModal: () => (
            showApprovePublicationBookingModal({
              publicationBooking,
            })
          ),
        },
        stateRef,
      }),
      checkRemovedBookingItems: () => checkRemovedBookingItems({
        callbacks: {
          showCreatePublicationOrderModal: () => (
            showCreatePublicationOrderModal({
              publication: selectedPublication,
              publicationEdition: selectedPublicationEdition,
            })
          ),
          showReviewRemovedPublicationBookingItemsModal: afterAction => (
            showReviewRemovedPublicationBookingItemsModal({
              afterAction,
              publicationOrderId: editionData.publicationOrderId,
            })
          ),
          removedBookingItems,
        },
        publicationOrderId: editionData.publicationOrderId,
      }),
      deletePublicationBooking: () => deletePublicationBooking({ filteredPublicationBookings }),
      performZoomAction: (actionType: ZoomActionType) => performZoomAction({
        localStorageKey, actionType, state, setState,
      }),
      selectPublicationBookingId: (payload: HandleCallbackActionParams) => selectPublicationBookingId(payload),
      selectZoomValue: (value: number) => changeZoom({ localStorageKey, zoom: value, setState }),
      setState,
      showApprovePublicationBookingModal: (
        payload: { isBulkRelease: boolean },
      ) => showApprovePublicationBookingModal({
        publicationBooking,
        ...payload,
      }),
      showCreateCampaignPublicationOrderModal: () => showCreateCampaignPublicationOrderModal({
        publicationEdition,
      }),
      showCreateOrEditPublicationBookingModal: () => showCreateOrEditPublicationBookingModal({
        callbacks: {
          // @ts-expect-error TS7019
          createPublicationBooking: async (...createParams) => {
            // @ts-expect-error TS2556
            const result = await createPublicationBooking(...createParams)
            if (result.success){
              const { id, publication_edition_id, publication_id } = result.data

              // @ts-expect-error TS2345
              selectPublicationBookingId({
                entityParams: {
                  id,
                  publication_edition_id,
                  publication_id,
                },
              })
            }

            return result
          },
        },
        publicationBooking: {
          publication_edition_id: publicationEditionId,
          publication_id: publicationId,
        },
      }),
      showManagePlannerBookingModal: () => showManagePlannerBookingModal({
        publicationBooking: selectedPublicationBooking,
      }),
      showSelectLayoutTemplateModal: () => showSelectLayoutTemplateModal({
        callbacks: {
          selectLayoutTemplateId: (payload: HandleCallbackActionParams) => selectLayoutTemplateId({
            payload,
            publicationBooking,
            updatePublicationBooking,
          }),
        },
        layoutTemplateId: publicationBooking.layout_template_id,
        publicationProductId: publicationBooking.publication_product_id,
        publicationId,
      }),
      showSelectPublicationModal: () => showSelectPublicationModal({
        callbacks: {
          selectPublicationId,
        },
      }),
      showSelectPublicationEditionModal: () => showSelectPublicationEditionModal({
        callbacks: {
          selectPublicationEditionId,
        },
        publicationId,
      }),
      zoomToBestFit: () => changeZoom({
        localStorageKey,
        setState,
        zoom: calculateOptimalZoom(height, width),
      }),
    },
    deadlineStatuses,
    editionData,
    filterString,
    filteredOrderItems,
    groupedEditionData,
    layoutTemplatesPayload,
    ordersPayload,
    publicationBookingItemsPayload,
    publicationBookingPayload,
    publicationBookingsPayload,
    publicationEditionPayload,
    publicationPayload,
    selectedBookingData,
    selectedBookingIndex: getSelectedBookingIndex({
      publicationBookings: filteredPublicationBookings,
      // @ts-expect-error TS2740
      selectedPublicationBooking,
    }),
    selectedPublicationBooking,
    selectedPublicationBookingId: publicationBookingId,
    sortItemsByKey,
    zoom,
  }
}

export type UsePublicationBookingPlannerPayload = ReturnType<typeof usePublicationBookingPlanner>

export default usePublicationBookingPlanner
